import { useTranslation } from 'react-i18next';
import { MenuItem } from '@material-ui/core';
import { TextField, Autocomplete } from '@avangard/ui/core';

import { usePageFilters, PageFiltersDrawer } from '@modules/shared/organisms';
import {
    useGeoFilters,
    useSchoolsFilter,
    useStudentSchoolClassesFilter,
} from '@modules/shared/hooks';
import { schoolTypeTranslates } from '@modules/schools/constants';
import { SchoolType } from '@modules/types/graphql';
import { eljurRoleOptions } from '@modules/eljur/constants';

import type { RegionEntity, LocationEntity } from '@modules/shared/entities';
import type { SchoolEntity } from '@modules/schools/entities';
import type { StudentsListFilters } from '@modules/students/hooks';

type DrawerFiltersProps = {
    isEljur?: boolean;
};

const schoolTypes = Object.values(SchoolType);

const DrawerFilters = (props: DrawerFiltersProps): React.ReactElement => {
    const { isEljur } = props;

    const { t } = useTranslation(['common', 'students']);

    const {
        filters,
        handleChange,
        handleChangeAutocomplete,

        getOptionValue,
        getOptionLabel,
        getOptionSelected,
    } = usePageFilters<StudentsListFilters>();

    const { regions, regionsLoading, locations, locationsCount, locationsLoading } = useGeoFilters({
        refRegionId: filters.schoolLocationRegionId,
    });

    const { schools, schoolsCount, schoolsLoading } = useSchoolsFilter({
        refLocationId: filters.schoolLocationId,
    });

    const { schoolClassesOptions, schoolClassesCount, schoolClassesLoading } =
        useStudentSchoolClassesFilter();

    return (
        <PageFiltersDrawer>
            <Autocomplete<RegionEntity | null, false, false, false>
                fullWidth
                id='schoolLocationRegionId'
                label={t('common:forms.labels.region') ?? ''}
                placeholder={t('common:forms.placeholder.choose_region') ?? ''}
                options={regions}
                value={getOptionValue(regions, filters.schoolLocationRegionId)}
                disabled={regionsLoading}
                getOptionLabel={getOptionLabel('name')}
                getOptionSelected={getOptionSelected()}
                onChange={handleChangeAutocomplete({ reset: ['schoolLocationId'] })}
            />

            <Autocomplete<LocationEntity | null, false, false, false>
                fullWidth
                id='schoolLocationId'
                label={t('common:forms.labels.location') ?? ''}
                placeholder={t('common:forms.placeholder.choose_location') ?? ''}
                options={locations}
                value={getOptionValue(locations, filters.schoolLocationId)}
                disabled={
                    !filters.schoolLocationRegionId || locationsCount === 0 || locationsLoading
                }
                getOptionLabel={getOptionLabel('name')}
                getOptionSelected={getOptionSelected()}
                onChange={handleChangeAutocomplete()}
            />

            <TextField
                fullWidth
                select
                id='schoolType'
                name='schoolType'
                label={t('common:forms.labels.school_type') ?? ''}
                placeholder={t('common:forms.placeholder.choose_school_type') ?? ''}
                value={filters.schoolType}
                onChange={handleChange}
            >
                <MenuItem value=''>{t('common:stubs.all')}</MenuItem>

                {schoolTypes.map(schoolType => (
                    <MenuItem key={schoolType} value={schoolType}>
                        {t(schoolTypeTranslates[schoolType])}
                    </MenuItem>
                ))}
            </TextField>

            <Autocomplete<SchoolEntity | null, false, false, false>
                fullWidth
                id='schoolId'
                label={t('common:forms.labels.school_short_name') ?? ''}
                placeholder={t('common:forms.placeholder.choose_school_short_name') ?? ''}
                options={schools}
                value={getOptionValue(schools, filters.schoolId)}
                disabled={!filters.schoolLocationId || schoolsCount === 0 || schoolsLoading}
                getOptionLabel={getOptionLabel('shortName')}
                getOptionSelected={getOptionSelected()}
                onChange={handleChangeAutocomplete()}
            />

            {isEljur ? (
                <>
                    <TextField
                        select
                        fullWidth
                        id='eljurRole'
                        name='eljurRole'
                        label={t('common:forms.labels.eljur_role')}
                        placeholder={t('common:forms.placeholder.choose_role') ?? ''}
                        onChange={handleChange}
                        value={filters.eljurRole}
                    >
                        <MenuItem value=''>{t('common:forms.placeholder.choose_role')}</MenuItem>

                        {eljurRoleOptions.map(eljurRole => (
                            <MenuItem key={eljurRole.key} value={eljurRole.key}>
                                {t(eljurRole.value)}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Autocomplete<AutocompleteOption<number | null | string>, false, false, false>
                        fullWidth
                        id='schoolClass'
                        label={t('common:forms.labels.school_class') ?? ''}
                        placeholder={t('common:forms.placeholder.choose_school_class') ?? ''}
                        options={schoolClassesOptions}
                        value={getOptionValue(schoolClassesOptions, filters.schoolClass, 'key')}
                        disabled={schoolClassesCount === 0 || schoolClassesLoading}
                        getOptionLabel={getOptionLabel('value')}
                        getOptionSelected={getOptionSelected('key')}
                        onChange={handleChangeAutocomplete({ extractSingleBy: 'key' })}
                    />
                </>
            ) : null}
        </PageFiltersDrawer>
    );
};

export { DrawerFilters };
